import { useEffect, useCallback, RefObject } from 'react';

interface UseAutoResizeTextareaOptions {
  isEmpty?: boolean;
  minHeight?: number;
  maxLines?: number;
  emptyLineHeight?: number;
  filledLineHeight?: number;
}

interface UseAutoResizeTextareaReturn {
  resetHeight: () => void;
}

/**
 * Custom hook to automatically resize a textarea based on its content
 * @param textareaRef - Ref to the textarea element
 * @param value - Current value of the textarea (triggers resize)
 * @param options - Configuration options for height calculation
 */
export const useAutoResizeTextarea = (
  textareaRef: RefObject<HTMLTextAreaElement>,
  value: string,
  options: UseAutoResizeTextareaOptions = {},
): UseAutoResizeTextareaReturn => {
  const { isEmpty = false, minHeight = 40, maxLines = 9, emptyLineHeight = 20, filledLineHeight = 24 } = options;

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset to auto to get accurate scrollHeight
    textarea.style.height = 'auto';

    const lineHeight = isEmpty ? emptyLineHeight : filledLineHeight;
    const maxHeight = lineHeight * maxLines;
    const scrollHeight = textarea.scrollHeight;

    // Clamp height between min and max
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Enable scrolling if content exceeds max height
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [textareaRef, isEmpty, minHeight, maxLines, emptyLineHeight, filledLineHeight]);

  const resetHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = `${minHeight}px`;
    }
  }, [textareaRef, minHeight]);

  // Adjust height when value or isEmpty changes
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Initialize height on mount or when isEmpty changes
  useEffect(() => {
    resetHeight();
  }, [isEmpty, resetHeight]);

  return { resetHeight };
};
